import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentSidebar = ({ 
  document, 
  versions, 
  collaborators, 
  comments,
  onVersionRestore,
  onCommentAdd,
  onCommentReply
}) => {
  const [activeTab, setActiveTab] = useState('metadata');
  const [newComment, setNewComment] = useState('');

  const tabs = [
    { id: 'metadata', label: 'Details', icon: 'Info' },
    { id: 'versions', label: 'Versions', icon: 'History' },
    { id: 'comments', label: 'Comments', icon: 'MessageSquare' },
    { id: 'collaborators', label: 'People', icon: 'Users' }
  ];

  const handleAddComment = () => {
    if (newComment?.trim()) {
      onCommentAdd(newComment);
      setNewComment('');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    if (!date) return 'Not available';
    return new Date(date)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col h-full">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon name={tab?.icon} size={16} />
                <span className="hidden sm:inline">{tab?.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'metadata' && (
          <div className="p-4 space-y-4">
            <div>
              <h3 className="font-medium text-foreground mb-3">Document Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-muted-foreground">Name</label>
                  <p className="text-sm font-medium text-foreground">{document?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Type</label>
                  <p className="text-sm text-foreground">{document?.type || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Size</label>
                  <p className="text-sm text-foreground">{formatFileSize(document?.size || document?.fileSize || 0)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Created</label>
                  <p className="text-sm text-foreground">{formatDate(document?.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Modified</label>
                  <p className="text-sm text-foreground">{formatDate(document?.updatedAt)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Owner</label>
                  <p className="text-sm text-foreground">{document?.ownerName || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Location</label>
                  <p className="text-sm text-foreground">
                    {document?.documentBaseName || 'Unknown'}
                    {document?.folderName && ` > ${document?.folderName}`}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Permissions</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Can Edit</span>
                  <Icon name="Check" size={16} className="text-success" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Can Share</span>
                  <Icon name="Check" size={16} className="text-success" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Can Comment</span>
                  <Icon name="Check" size={16} className="text-success" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'versions' && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-foreground">Version History</h3>
              <Button variant="ghost" size="sm" iconName="RefreshCw" iconSize={16} />
            </div>
            <div className="space-y-3">
              {versions?.map((version) => (
                <div key={version?.id} className="border border-border rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Version {version?.number}
                        {version?.isCurrent && (
                          <span className="ml-2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                            Current
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDate(version?.createdAt)}</p>
                    </div>
                    {!version?.isCurrent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onVersionRestore(version?.id)}
                        iconName="RotateCcw"
                        iconSize={14}
                      />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">By {version?.author}</p>
                  {version?.changes && (
                    <p className="text-xs text-muted-foreground">{version?.changes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-foreground">Comments</h3>
              <span className="text-sm text-muted-foreground">{comments?.length}</span>
            </div>

            {/* Add Comment */}
            <div className="mb-4 p-3 border border-border rounded-lg">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e?.target?.value)}
                placeholder="Add a comment..."
                className="w-full p-2 text-sm border border-border rounded resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
              <div className="flex justify-end mt-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAddComment}
                  disabled={!newComment?.trim()}
                >
                  Comment
                </Button>
              </div>
            </div>

            {/* Comments List */}
            <div className="space-y-3">
              {comments?.map((comment) => (
                <div key={comment?.id} className="border border-border rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs text-secondary-foreground font-medium">
                        {comment?.author?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-foreground">{comment?.author}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(comment?.createdAt)}</p>
                      </div>
                      <p className="text-sm text-foreground">{comment?.content}</p>
                      {comment?.replies && comment?.replies?.length > 0 && (
                        <div className="mt-3 pl-4 border-l-2 border-border space-y-2">
                          {comment?.replies?.map((reply) => (
                            <div key={reply?.id} className="flex items-start gap-2">
                              <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xs text-muted-foreground font-medium">
                                  {reply?.author?.split(' ')?.map(n => n?.[0])?.join('')}
                                </span>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">{reply?.author}</p>
                                <p className="text-sm text-foreground">{reply?.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'collaborators' && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-foreground">Collaborators</h3>
              <Button variant="ghost" size="sm" iconName="UserPlus" iconSize={16} />
            </div>
            <div className="space-y-3">
              {collaborators?.map((collaborator) => (
                <div key={collaborator?.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                  <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                    <span className="text-sm text-secondary-foreground font-medium">
                      {collaborator?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{collaborator?.name}</p>
                    <p className="text-xs text-muted-foreground">{collaborator?.role}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {collaborator?.isOnline && (
                      <div className="w-2 h-2 bg-success rounded-full" />
                    )}
                    <Button variant="ghost" size="sm" iconName="MoreVertical" iconSize={16} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSidebar;